import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { systemPromptsApi } from '../services/api';

interface SystemPrompt {
  key: string;
  displayName: string;
  currentValue: string;
  defaultValue: string;
  isCustom: boolean;
  category: string;
  description: string;
}

const SystemPrompts: React.FC = () => {
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<SystemPrompt | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemPromptsApi.getAll();
      setPrompts(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der System-Prompts:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der System-Prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prompt: SystemPrompt) => {
    setEditingPrompt(prompt);
    setEditValue(prompt.currentValue);
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    try {
      setSaving(true);
      await systemPromptsApi.update(editingPrompt.key, { promptValue: editValue });
      await loadPrompts();
      setEditingPrompt(null);
      alert('System-Prompt erfolgreich aktualisiert');
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (prompt: SystemPrompt) => {
    if (!window.confirm(`System-Prompt "${prompt.displayName}" auf Standardwert zurücksetzen?`)) {
      return;
    }

    try {
      await systemPromptsApi.reset(prompt.key);
      await loadPrompts();
      alert('System-Prompt erfolgreich zurückgesetzt');
    } catch (err: any) {
      console.error('Fehler beim Zurücksetzen:', err);
      alert('Fehler beim Zurücksetzen: ' + (err.response?.data?.message || err.message));
    }
  };

  // Gruppiere Prompts nach Kategorie
  const groupedPrompts = prompts.reduce((acc, prompt) => {
    if (!acc[prompt.category]) {
      acc[prompt.category] = [];
    }
    acc[prompt.category].push(prompt);
    return acc;
  }, {} as Record<string, SystemPrompt[]>);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">KI System-Prompts</Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>System-Prompts</strong> definieren, wie die KI auf verschiedene Anfragen reagiert.
          Sie können die Standard-Prompts anpassen, um das Verhalten der KI für E-Mails, Dokumente und Analysen zu steuern.
        </Typography>
      </Alert>

      {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => (
        <Box key={category} mb={3}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon color="primary" />
            {category}
          </Typography>

          {categoryPrompts.map((prompt) => (
            <Accordion key={prompt.key} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography sx={{ flexGrow: 1 }}>
                    <strong>{prompt.displayName}</strong>
                  </Typography>
                  {prompt.isCustom && (
                    <Chip label="Angepasst" color="primary" size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {prompt.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {prompt.description}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Aktueller Wert:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {prompt.currentValue}
                    </Paper>
                  </Box>

                  {prompt.isCustom && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Standardwert:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: 'info.light',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {prompt.defaultValue}
                      </Paper>
                    </Box>
                  )}

                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(prompt)}
                    >
                      Bearbeiten
                    </Button>
                    {prompt.isCustom && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ResetIcon />}
                        onClick={() => handleReset(prompt)}
                      >
                        Zurücksetzen
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}

      {/* Edit Dialog */}
      <Dialog
        open={editingPrompt !== null}
        onClose={() => setEditingPrompt(null)}
        maxWidth="md"
        fullWidth
      >
        {editingPrompt && (
          <>
            <DialogTitle>
              System-Prompt bearbeiten: {editingPrompt.displayName}
            </DialogTitle>
            <DialogContent>
              <Box mt={1}>
                {editingPrompt.description && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {editingPrompt.description}
                  </Alert>
                )}

                <TextField
                  label="System-Prompt"
                  multiline
                  rows={12}
                  fullWidth
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    },
                  }}
                />

                {editingPrompt.isCustom && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Standardwert (zum Vergleich):
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}
                    >
                      {editingPrompt.defaultValue}
                    </Paper>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setEditingPrompt(null)}
                startIcon={<CloseIcon />}
              >
                Abbrechen
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !editValue.trim()}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Speichere...' : 'Speichern'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SystemPrompts;
